import React, { useRef } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, GripVertical, ImagePlus, Type } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export function CarouselBuilder({ slides, activeSlide, onSetActiveSlide, onAddSlide, onRemoveSlide, onUpdateSlide, onReorderSlide, onOpenLibrary }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    onUpdateSlide(activeSlide, { imageUrl: url, imageFile: file });
    e.target.value = '';
  };

  const current = slides[activeSlide];

  return (
    <div className="space-y-5" data-testid="carousel-builder-controls">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />

      {/* Slide Navigator */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold uppercase tracking-wider text-[#64748b]">
            Slides ({slides.length})
          </label>
          <Button variant="outline" size="sm" onClick={onAddSlide} className="border-[#e2e8f0] h-7 px-2 text-xs" data-testid="add-slide-btn">
            <Plus className="w-3 h-3 mr-1" /> Add Slide
          </Button>
        </div>

        {/* Slide Thumbnails */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => onSetActiveSlide(i)}
              className={`relative flex-shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all ${
                activeSlide === i ? 'border-[#2563eb] ring-2 ring-[#2563eb]/20' : 'border-[#e2e8f0]'
              }`}
              data-testid={`slide-thumb-${i}`}
            >
              {slide.imageUrl ? (
                <img src={slide.imageUrl} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#f8fafc]" style={{ backgroundColor: slide.backgroundColor || '#f8fafc' }}>
                  <span className="text-[10px] font-bold text-[#94a3b8]">{i + 1}</span>
                </div>
              )}
              {activeSlide === i && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563eb]" />
              )}
            </button>
          ))}
        </div>

        {/* Slide Navigation */}
        <div className="flex items-center justify-between mt-2">
          <Button
            variant="ghost" size="sm"
            onClick={() => onSetActiveSlide(Math.max(0, activeSlide - 1))}
            disabled={activeSlide === 0}
            className="h-7 px-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs font-medium text-[#64748b]">
            Slide {activeSlide + 1} of {slides.length}
          </span>
          <Button
            variant="ghost" size="sm"
            onClick={() => onSetActiveSlide(Math.min(slides.length - 1, activeSlide + 1))}
            disabled={activeSlide === slides.length - 1}
            className="h-7 px-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Current Slide Editor */}
      {current && (
        <div className="space-y-4 pt-3 border-t border-[#e2e8f0]">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wider text-[#2563eb]">
              Slide {activeSlide + 1}
            </label>
            {slides.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => onRemoveSlide(activeSlide)} className="text-red-400 hover:text-red-600 h-7 px-2" data-testid="remove-slide-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            )}
          </div>

          {/* Image */}
          <div>
            <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider block mb-1.5">Image</span>
            {current.imageUrl ? (
              <div className="relative rounded-lg overflow-hidden h-24">
                <img src={current.imageUrl} alt="Slide" className="w-full h-full object-cover" />
                <button
                  onClick={() => onUpdateSlide(activeSlide, { imageUrl: null, imageFile: null })}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {onOpenLibrary && (
                  <Button variant="outline" onClick={onOpenLibrary} className="border-[#2563eb]/30 text-[#2563eb] h-16 flex-col gap-1" data-testid="slide-from-library-btn">
                    <ImagePlus className="w-4 h-4" />
                    <span className="text-xs">Library</span>
                  </Button>
                )}
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="border-dashed border-[#e2e8f0] h-16 flex-col gap-1" data-testid="slide-add-image-btn">
                  <ImagePlus className="w-4 h-4 text-[#94a3b8]" />
                  <span className="text-xs text-[#94a3b8]">Upload</span>
                </Button>
              </div>
            )}
          </div>

          {/* Caption */}
          <div>
            <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider block mb-1.5">Caption (optional)</span>
            <Input
              value={current.caption || ''}
              onChange={(e) => onUpdateSlide(activeSlide, { caption: e.target.value })}
              placeholder="Slide caption..."
              className="border-[#e2e8f0] text-sm"
              data-testid="slide-caption-input"
            />
          </div>

          {/* Background Color */}
          <div>
            <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider block mb-1.5">Background</span>
            <div className="flex gap-1.5">
              {['#ffffff', '#1e3a5f', '#2563eb', '#0f172a', '#f8fafc', '#c5d93d'].map(color => (
                <button
                  key={color}
                  onClick={() => onUpdateSlide(activeSlide, { backgroundColor: color })}
                  className={`w-7 h-7 rounded-lg border-2 transition-transform hover:scale-110 ${
                    current.backgroundColor === color ? 'border-[#2563eb] scale-110' : 'border-[#e2e8f0]'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Reorder */}
          {slides.length > 1 && (
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => onReorderSlide(activeSlide, activeSlide - 1)}
                disabled={activeSlide === 0}
                className="flex-1 border-[#e2e8f0] text-xs"
              >
                <ChevronLeft className="w-3 h-3 mr-1" /> Move Left
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => onReorderSlide(activeSlide, activeSlide + 1)}
                disabled={activeSlide === slides.length - 1}
                className="flex-1 border-[#e2e8f0] text-xs"
              >
                Move Right <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
